/**
 * Verification API client
 *
 * Handles partner verification requests and admin review
 */

import { apiClient } from "./baseClient";

/**
 * Verification status type
 */
export type VerificationStatus =
  | "unverified"
  | "pending"
  | "verified"
  | "rejected";

/**
 * Partner account type (Japanese real estate)
 */
export type PartnerAccountType = "corporate" | "individual";

/**
 * Verification document URLs
 */
export interface VerificationDocuments {
  agency_license_url?: string | null;
  officer_id_url?: string | null;
}

/**
 * Verification status response
 */
export interface VerificationStatusResponse {
  org_id: string;
  status: VerificationStatus;
  has_active_request: boolean;
  current_request_id?: string | null;
  rejection_reason?: string | null;
}

/**
 * Verification submit request
 */
export interface VerificationSubmitRequest {
  account_type: PartnerAccountType;
  company_name: string;
  license_number: string;
  corporate_email: string;
  official_website?: string | null;
  documents: VerificationDocuments;
}

/**
 * Verification submit response
 */
export interface VerificationSubmitResponse {
  ok: boolean;
  request_id: string;
  status: VerificationStatus;
  message: string;
}

/**
 * Verification request summary (admin)
 */
export interface VerificationRequestSummary {
  request_id: string;
  org_id: string;
  org_name: string;
  requested_by_uid: string;
  requested_by_name: string;
  requested_by_email: string;
  status: VerificationStatus;
  account_type: PartnerAccountType;
  submitted_at: string;
}

/**
 * Verification request list response (admin)
 */
export interface VerificationRequestListResponse {
  requests: VerificationRequestSummary[];
  total: number;
}

/**
 * Verification request detail response (admin)
 */
export interface VerificationRequestDetailResponse {
  request_id: string;
  org_id: string;
  org_name: string;
  requested_by_uid: string;
  requested_by_name: string;
  requested_by_email: string;
  status: VerificationStatus;
  account_type: PartnerAccountType;
  company_name: string;
  license_number: string;
  corporate_email: string;
  official_website?: string | null;
  documents: VerificationDocuments;
  submitted_at: string;
  reviewed_at?: string | null;
  reviewed_by_uid?: string | null;
  reviewed_by_name?: string | null;
  rejection_reason?: string | null;
}

/**
 * Verification approve response (admin)
 */
export interface VerificationApproveResponse {
  ok: boolean;
  request_id: string;
  org_id: string;
  status: VerificationStatus;
  message: string;
}

/**
 * Verification reject request (admin)
 */
export interface VerificationRejectRequest {
  reason: string;
}

/**
 * Verification reject response (admin)
 */
export interface VerificationRejectResponse {
  ok: boolean;
  request_id: string;
  org_id: string;
  status: VerificationStatus;
  message: string;
}

// ================================
// Partner Endpoints
// ================================

/**
 * Get verification status for the current partner organization
 */
export async function getVerificationStatus(
  token: string,
): Promise<VerificationStatusResponse> {
  return apiClient.get<VerificationStatusResponse>("/verification/status", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Submit a new verification request
 */
export async function submitVerification(
  token: string,
  request: VerificationSubmitRequest,
): Promise<VerificationSubmitResponse> {
  return apiClient.post<VerificationSubmitResponse>(
    "/verification/submit",
    request,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

// ================================
// Admin/Developer Endpoints
// ================================

/**
 * List all verification requests (admin/developer only)
 */
export async function listVerificationRequests(
  token: string,
): Promise<VerificationRequestListResponse> {
  return apiClient.get<VerificationRequestListResponse>(
    "/verification/requests",
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

/**
 * Get detailed information about a verification request (admin/developer only)
 */
export async function getVerificationRequestDetail(
  token: string,
  requestId: string,
): Promise<VerificationRequestDetailResponse> {
  return apiClient.get<VerificationRequestDetailResponse>(
    `/verification/requests/${requestId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

/**
 * Approve a verification request (admin/developer only)
 */
export async function approveVerificationRequest(
  token: string,
  requestId: string,
): Promise<VerificationApproveResponse> {
  return apiClient.post<VerificationApproveResponse>(
    `/verification/requests/${requestId}/approve`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

/**
 * Reject a verification request (admin/developer only)
 */
export async function rejectVerificationRequest(
  token: string,
  requestId: string,
  reason: string,
): Promise<VerificationRejectResponse> {
  return apiClient.post<VerificationRejectResponse>(
    `/verification/requests/${requestId}/reject`,
    { reason },
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

// ================================
// Type Aliases for convenience
// ================================

/**
 * Alias for verification request detail - used in admin UI
 */
export type VerificationRequest = VerificationRequestDetailResponse;
